<template>
  <div>
    <spinner v-if="isLoading" class="m-auto mt-64" :width="48" :height="48" />
    <div v-else-if="showSection" data-testid="getting-started">
      <div class="flex justify-between items-end mb-12">
        <h2 class="text-h3">Getting Started</h2>
        <p class="text-h4">{{ completenessPercentage }}% complete</p>
      </div>
      <progress-bar
        :max="steps.length"
        :value="completedSteps"
        :content="`${completenessPercentage} percent complete`"
        class="mb-20"
      />
      <step-card-list :items="steps" />
      <hr class="my-40 border-grey-divider-subdued" />
    </div>
    <confetti v-if="showConfetti" class="fixed left-0 bottom-0 !h-auto z-[90]" />
  </div>
</template>

<script lang="ts">
import ProgressBar from '@/components/progress_bar/ProgressBar.vue';
import StepCardList from '@/components/step_card_list/StepCardList.vue';
import Spinner from '@/components/spinner/Spinner.vue';
import Confetti from '@/components/confetti/Confetti.vue';
import { GETTING_STARTED_REQUESTS_QUERY } from '@/graphql/bookings/queries';
import { Account } from '@/models/account';
import { FETCH_SPONSORED_BGC_COUNT } from '@/graphql/account/queries';

export default {
  name: 'GettingStarted',
  components: {
    ProgressBar,
    StepCardList,
    Spinner,
    Confetti,
  },
  props: {
    account: {
      required: true,
      type: Account,
    },
    photoUrl: {
      required: false,
      type: String,
      default: undefined,
    },
  },
  data() {
    return {
      jobInfo: {} as any,
      isLoading: true,
      showConfetti: false,
      hideSection: false,
      sponsoredChecks: 0,
    };
  },
  computed: {
    showSection(): boolean {
      return !this.hideSection;
    },
    completedSteps(): number {
      return this.steps.filter((step) => step.variant === 'completedTask').length;
    },
    completenessPercentage(): number | null {
      if (this.isLoading) return null;
      return Math.floor((this.completedSteps / this.steps.length) * 100);
    },
    hasJob(): boolean {
      return (
        !!this.jobInfo &&
        Object.keys(this.jobInfo).length > 0 &&
        new Date(this.jobInfo?.expiresAt).toISOString() > new Date().toISOString()
      );
    },
    steps(): Array<any> {
      const isSpecialNeeds = this.jobInfo?.careType?.toLowerCase() === 'special_needs';
      const maxRate = Math.floor(this.jobInfo?.paymentRateMax / 100);
      const maxRateParam = maxRate ? `&rate=${maxRate}` : '';
      const distanceParam = this.jobInfo?.distance ? `&distance=${this.jobInfo?.distance}` : '';
      const sitterSearchLink = isSpecialNeeds
        ? `/profile/provider/search?search_strategy=special_needs&zipcode=${this.jobInfo?.location?.zipcode}`
        : `/availability/search?zipcode=${this.jobInfo?.location?.zipcode}${distanceParam}${maxRateParam}&careType=${this.jobInfo?.careType?.toLowerCase()}`;
      const isPhotoComplete = !!this.photoUrl;
      const arr = [
        {
          variant: this.hasJob ? 'completedTask' : 'incompleteTask',
          title: 'Post your needs',
          content: this.hasJob
            ? 'Great work! Posting your needs is the first step to finding the perfect sitter.'
            : 'Post a job to start finding the perfect sitter for your family.',
          linkURL: this.hasJob ? undefined : '/job-post',
          disabled: this.hasJob,
        },
      ] as any;
      if (this.hasJob)
        arr.push({
          variant: this.jobInfo?.hasRecruitedSittersForJob ? 'completedTask' : 'incompleteTask',
          title: 'Invite sitters to apply',
          content: 'See someone you like? A personal invite can help you connect with great caregivers faster.',
          linkURL: this.jobInfo?.hasRecruitedSittersForJob ? undefined : sitterSearchLink,
          disabled: this.jobInfo?.hasRecruitedSittersForJob,
          useFullPageNavigation: !!isSpecialNeeds,
        });
      arr.push({
        variant: isPhotoComplete ? 'completedTask' : 'incompleteTask',
        title: 'Showcase your family',
        content: this.hasJob
          ? 'Sitters are more likely to respond to families that have photos.'
          : 'Add a photo so sitters can get to know your family when you post a job.',
        linkURL: isPhotoComplete ? undefined : '/account/seekers/photo#/upload',
        disabled: isPhotoComplete,
        useFullPageNavigation: true,
      });
      if (!this.hasJob)
        arr.push({
          variant: 'incompleteTask',
          title: 'Explore sitters',
          content: 'Browse and favorite sitters so you’re ready when care is needed.',
          linkURL: '/availability/search',
          disabled: false,
        });
      if (this.account.isCorporate() || this.account.isPremium())
        arr.push({
          variant: this.sponsoredChecks > 0 ? 'completedTask' : 'incompleteTask',
          title: 'Discover your background check benefit',
          content: 'See what’s included with your membership and how to get started.',
          linkURL: this.sponsoredChecks > 0 ? null : '/background-checks/overview?from=checklist',
          disabled: this.sponsoredChecks > 0,
        });
      return arr.sort((a, b) => !!(b.variant === 'incompleteTask') - !!(a.variant === 'incompleteTask'));
    },
  },
  watch: {
    completenessPercentage: {
      handler(newValue) {
        // Don't show confetti if we're still loading the query that returns the user has invited sitters or not
        if (this.isLoading) return;

        // Retrieve if the user has accessed dashboard before with completion at less than 100% (needToShowConfetti = true)
        // If they have, then we know to show confetti when they hit 100% and hide the section after.
        // If they haven't, then we won't show confetti and we will still hide the section when they hit 100%
        const STORAGE_KEY = 'show_getting_started_confetti';
        const needToShowConfetti = sessionStorage.getItem(STORAGE_KEY) === this.account.id;

        // Only show confetti if the user completes all steps and they have accessed the dashboard before without completing all steps.
        // If completion is at 100% and they haven't accessed the dashboard with less than 100% completion, just hide the section without showing confetti again.
        // (this is to prevent over-showing the confetti)
        if (newValue === 100 && needToShowConfetti) {
          this.showConfetti = true;
          // Timeout to compensate for confetti animation duration so that the section doesn't disappear before the animation finishes
          setTimeout(() => {
            this.showConfetti = false;
            // Remove the flag in sessionStorage so that if the user accesses the dashboard again with 100% completion, they will not see the confetti again
            sessionStorage.removeItem('show_getting_started_confetti');
            // Emit event to parent component to hide the Getting Started section with fade and slide animation after confetti animation plays
            this.$emit('hideGettingStarted');
          }, 2500);
          // If the user is at 100% completion but they haven't accessed the dashboard before with less than 100% completion, just hide the section without showing confetti to prevent confetti from showing every time they access the dashboard with 100% completion
        } else if (newValue === 100 && !needToShowConfetti) {
          this.hideSection = true;
        }
        // If the completion is less than 100%, and we haven't already stored that, store a variable so that we know to show confetti when they hit 100% next time, and to prevent showing confetti if they have already completed all steps before
        if (newValue < 100 && !needToShowConfetti) {
          sessionStorage.setItem('show_getting_started_confetti', this.account.id);
        }
      },
      immediate: true,
    },
  },
  mounted() {
    this.fetchRequests();
  },
  methods: {
    async fetchRequests() {
      try {
        const response = await this.$apollo.query({
          query: GETTING_STARTED_REQUESTS_QUERY,
          variables: { accountId: this.account.id },
        });
        const count = await this.$apollo.query({
          query: FETCH_SPONSORED_BGC_COUNT,
        });
        this.sponsoredChecks = count.data.viewer.sponsoredBackgroundChecks?.totalCount;
        this.jobInfo = response.data.account.jobs.edges[0]?.node ?? null;
        this.isLoading = false;
      } catch (_error) {
        this.isLoading = false;
      }
    },
  },
};
</script>
